import * as styles from "./ContractFields.css";

import { useAtomValue } from "jotai";
import type { JSX, ReactNode } from "react";

import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import type { DateRangeValue } from "@frontend/components/atoms/DatePicker";
import {
  Field,
  FieldContent,
  FieldContext,
  FieldError,
  FieldLabel,
  makeFieldContext,
} from "@frontend/components/atoms/Field";
import { Fieldset, FieldsetLegend } from "@frontend/components/atoms/Fieldset";
import type { IconName } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { NumberField } from "@frontend/components/atoms/NumberField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { Switch } from "@frontend/components/atoms/Switch";
import { useField } from "@frontend/hooks/form";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { ReferenceField } from "@frontend/shared/reference-field";
import { getAmountMask } from "@frontend/utils/masks";
import { Trans, useLingui } from "@lingui/react/macro";

import { contractForm, optionsAtom } from "../stores/editor";

const fields = contractForm.fields;

const condition = fields.commercialCondition.fields;

const DEFAULT_WORKED_WEEKS = 38;

const OPEN_END = "open";

const WEEKS_IN_YEAR = 52;

const gridFields = makeFieldContext({
  orientation: "vertical",
  grouped: false,
});

function Group({
  icon,
  legend,
  children,
}: {
  icon: IconName;
  legend: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <Fieldset variant="section">
      <FieldsetLegend icon={icon}>{legend}</FieldsetLegend>

      <FieldContext.Provider value={gridFields}>
        <div className={styles.fields}>{children}</div>
      </FieldContext.Provider>
    </Fieldset>
  );
}

export function ContractFields(): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const options = useAtomValue(optionsAtom);

  const dateBegin = useField(fields.dateBegin);
  const dateEnd = useField(fields.dateEnd);
  const autoRenewal = useField(fields.autoRenewal);
  const renewalWarning = useField(fields.renewalWarning);
  const workedWeeksCount = useField(fields.workedWeeksCount);
  const postPeriodBilling = useField(fields.postPeriodBilling);
  const price = useField(fields.price);
  const priceType = useField(fields.priceType);
  const paymentType = useField(condition.paymentTypeId);
  const paymentEmail = useField(condition.paymentEmail);
  const billingTime = useField(condition.subscriptionBillingTimeId);
  const priceRevision = useField(condition.priceRevisionActivation);

  const chosenPaymentType =
    options.paymentTypes.find(({ id }) => id === paymentType.value) ?? null;

  const hourly = "hour" === priceType.value;

  const wholeYear = null == workedWeeksCount.value;

  function chooseWholeYear(next: boolean) {
    workedWeeksCount.setValue(next ? null : DEFAULT_WORKED_WEEKS);
  }

  const period: DateRangeValue = [
    dateBegin.value ?? null,
    dateEnd.value ?? (dateBegin.value ? OPEN_END : null),
  ];

  function choosePeriod([begin, end]: DateRangeValue) {
    dateBegin.setValue(begin);
    dateEnd.setValue(OPEN_END === end ? null : end);
  }

  const monthEnd =
    options.subscriptionBillingTimes.find(({ type }) => "monthend" === type) ??
    null;

  function chooseHourly(next: string | null) {
    priceType.setValue("hour" === next || "flat" === next ? next : null);

    if ("hour" === next && monthEnd) {
      billingTime.setValue(monthEnd.id);
    }
  }

  return (
    <div className={styles.groups}>
      <Group icon="FileText" legend={t`Contract`}>
        <ReferenceField
          className={styles.span.third}
          label={t`Group`}
          placeholder={t`Please choose an option`}
          items={options.contractGroups}
          field={fields.contractGroupId}
        />

        <ReferenceField
          className={styles.span.third}
          label={t`Notice period`}
          items={options.endDelays}
          field={fields.endDelayId}
        />

        <ReferenceField
          className={styles.span.third}
          label={t`Termination reason`}
          placeholder={t`Please choose an option`}
          items={options.terminationReasons}
          field={fields.terminationReasonId}
        />

        <Field className={styles.span.third}>
          <FieldLabel>{t`Period`}</FieldLabel>

          <FieldContent>
            <DatePicker
              range
              requireEnd
              openEnd={{ label: t`Until further notice`, value: OPEN_END }}
              locale={locale}
              value={period}
              onValueChange={choosePeriod}
            >
              <DatePickerTrigger>
                <DatePickerValue placeholder={t`Enter a date`} />
              </DatePickerTrigger>

              <DatePickerContent />
            </DatePicker>

            <FieldError match={Boolean(dateBegin.error || dateEnd.error)}>
              {dateBegin.error ?? dateEnd.error}
            </FieldError>
          </FieldContent>
        </Field>

        <ReferenceField
          className={styles.span.third}
          label={t`Duration`}
          placeholder={t`Please choose an option`}
          items={options.contractDurations}
          field={fields.contractDurationId}
        />

        <Field className={styles.span.third}>
          <FieldLabel>{t`Weeks worked a year`}</FieldLabel>

          <FieldContent>
            <NumberField
              value={wholeYear ? WEEKS_IN_YEAR : workedWeeksCount.value}
              disabled={wholeYear}
              toggle={{
                label: t`Whole year`,
                checked: wholeYear,
                onCheckedChange: chooseWholeYear,
              }}
              onValueChange={(next) =>
                workedWeeksCount.setValue(next ?? DEFAULT_WORKED_WEEKS)
              }
              min={1}
              max={52}
            />

            <FieldError match={Boolean(workedWeeksCount.error)}>
              {workedWeeksCount.error}
            </FieldError>
          </FieldContent>
        </Field>

        <div className={styles.switchCell}>
          <Switch
            label={t`Automatic renewal`}
            checked={true === autoRenewal.value}
            onCheckedChange={(next) => autoRenewal.setValue(next)}
          />
        </div>

        <div className={styles.switchCellLast}>
          <Switch
            label={t`Renewal warning`}
            checked={true === renewalWarning.value}
            onCheckedChange={(next) => renewalWarning.setValue(next)}
          />
        </div>
      </Group>

      <Group icon="Banknote" legend={t`Billing`}>
        <ReferenceField
          className={styles.span.quarter}
          label={t`Payment term`}
          items={options.paymentPeriods}
          field={condition.subscriptionPaymentPeriodId}
        />

        <Field
          className={styles.span.quarter}
          invalid={Boolean(paymentType.error)}
        >
          <FieldLabel>{t`Payment type`}</FieldLabel>

          <FieldContent>
            <Select
              items={options.paymentTypes.map((item) => ({
                label: item.label,
                value: String(item.id),
              }))}
              value={
                null == paymentType.value ? null : String(paymentType.value)
              }
              onValueChange={(next) =>
                paymentType.setValue(null === next ? null : Number(next))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {options.paymentTypes.map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <FieldError match={Boolean(paymentType.error)}>
              {paymentType.error}
            </FieldError>
          </FieldContent>
        </Field>

        <ReferenceField
          className={styles.span.sixth}
          label={t`Payable at`}
          items={options.paymentTargets}
          field={condition.paymentTargetId}
        />

        {chosenPaymentType?.needEmail ? (
          <Field
            className={styles.span.email}
            invalid={Boolean(paymentEmail.error)}
          >
            <FieldLabel>{t`Billing email address`}</FieldLabel>

            <FieldContent>
              <Input
                type="email"
                value={paymentEmail.value ?? ""}
                onValueChange={(next) =>
                  paymentEmail.setValue("" === next ? null : next)
                }
              />

              <FieldError match={Boolean(paymentEmail.error)}>
                {paymentEmail.error}
              </FieldError>
            </FieldContent>
          </Field>
        ) : null}

        <Field
          className={styles.span.quarter}
          invalid={Boolean(billingTime.error)}
        >
          <FieldLabel>{t`Billing moment`}</FieldLabel>

          <FieldContent>
            <Select
              items={options.subscriptionBillingTimes.map((item) => ({
                label: item.label,
                value: String(item.id),
              }))}
              value={
                null == billingTime.value ? null : String(billingTime.value)
              }
              disabled={hourly}
              onValueChange={(next) =>
                billingTime.setValue(null === next ? null : Number(next))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {options.subscriptionBillingTimes.map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <FieldError match={Boolean(billingTime.error)}>
              {billingTime.error}
            </FieldError>
          </FieldContent>
        </Field>

        <ReferenceField
          className={styles.span.quarter}
          label={t`Billing period`}
          items={options.billPeriods}
          field={fields.billPeriodId}
        />

        <Field className={styles.price} invalid={Boolean(price.error)}>
          <FieldLabel>{t`Price`}</FieldLabel>

          <FieldContent>
            <Input
              inputMode="decimal"
              mask={getAmountMask()}
              value={price.value ?? ""}
              rightSection={<span className={styles.suffix}>{t`Frs`}</span>}
              onValueChange={(next) =>
                price.setValue("" === next ? null : next)
              }
            />

            <FieldError match={Boolean(price.error)}>{price.error}</FieldError>
          </FieldContent>
        </Field>

        <Field className={styles.span.sixth} invalid={Boolean(priceType.error)}>
          <FieldLabel>{t`Price mode`}</FieldLabel>

          <FieldContent>
            <Select
              items={options.priceTypes.map((item) => ({
                label: item.label,
                value: item.value,
              }))}
              value={priceType.value ?? null}
              onValueChange={chooseHourly}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {options.priceTypes.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <FieldError match={Boolean(priceType.error)}>
              {priceType.error}
            </FieldError>
          </FieldContent>
        </Field>

        <div className={styles.switches}>
          <Switch
            label={t`Billed at the end of the period`}
            checked={true === postPeriodBilling.value}
            onCheckedChange={(next) => postPeriodBilling.setValue(next)}
          />

          <Switch
            label={t`Yearly price revision`}
            checked={true === priceRevision.value}
            onCheckedChange={(next) => priceRevision.setValue(next)}
          />

          <span className={styles.hint}>
            <Trans>
              The amendments on their tab take over on their own effective date.
            </Trans>
          </span>
        </div>
      </Group>
    </div>
  );
}
